import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";

/**
 * Hooks around /api/v1/contact-info/singleton/ — admin-only edit of the
 * single ContactInfo row that powers the public /contact/ page + footer.
 */

export interface ContactInfoRow {
  id: number;
  office_name: string;
  intro_text: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  country: string;
  phone_primary: string;
  phone_secondary: string;
  email: string;
  website: string;
  office_hours: string;
  map_embed_url: string;
  facebook_url: string;
  twitter_url: string;
  linkedin_url: string;
  youtube_url: string;
  updated_at: string;
}

const KEY = ["contact-info", "singleton"];

export function useContactInfo() {
  return useQuery<ContactInfoRow>({
    queryKey: KEY,
    queryFn: async () => {
      const { data } = await api.get<ContactInfoRow>("/contact-info/singleton/");
      return data;
    },
    staleTime: 60_000,
  });
}

export function useUpdateContactInfo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<Omit<ContactInfoRow, "id" | "updated_at">>) => {
      const { data } = await api.patch<ContactInfoRow>("/contact-info/singleton/", patch);
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(KEY, data);
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
